/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the grid table, segmented control and toolbar helpers.
 * @state-owner none
 * @allowed-imports React
 * @forbidden business rules; network; store access
 * @public-api workspace modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { MouseEvent, ReactNode } from 'react';
import { clickable } from '../lib/interactive';

/**
 * Collects positional table cells. `VTable` assigns the grid keys, so the nodes
 * themselves never carry one — this factory keeps that intent explicit.
 */
export function tableCells(...nodes: readonly ReactNode[]): readonly ReactNode[] {
  return nodes;
}

export interface TableRow {
  readonly key: string;
  readonly cells: readonly ReactNode[];
  readonly on?: boolean;
  readonly dim?: boolean;
  readonly onSelect?: () => void;
  readonly onContextMenu?: (event: MouseEvent<HTMLDivElement>) => void;
}

export function VTable({
  template,
  head,
  rows,
  empty = '沒有資料',
}: {
  template: string;
  head?: readonly ReactNode[] | null;
  rows: readonly TableRow[];
  empty?: ReactNode;
}) {
  return (
    <div className="vtb" style={{ ['--tc' as string]: template }}>
      {head ? (
        <div className="vth">
          {head.map((cell, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: header cells are positional
            <span key={index}>{cell}</span>
          ))}
        </div>
      ) : null}
      {rows.length ? (
        rows.map((row) => (
          // biome-ignore lint/a11y/noStaticElementInteractions: rows host their own buttons, so the row cannot itself be a <button>
          <div
            className={`vtr${row.on ? ' on' : ''}${row.dim ? ' dim' : ''}`}
            key={row.key}
            onContextMenu={row.onContextMenu}
            role={row.onSelect ? 'button' : undefined}
            tabIndex={row.onSelect ? 0 : undefined}
            {...(row.onSelect ? clickable<HTMLDivElement>(row.onSelect) : {})}
          >
            {row.cells.map((cell, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: table cells are positional
              <span key={index}>{cell}</span>
            ))}
          </div>
        ))
      ) : (
        <div className="vtempty">{empty}</div>
      )}
    </div>
  );
}

export interface SegmentOption {
  readonly value: string;
  readonly label: ReactNode;
}

/** The control. */
export function VSegment({
  options,
  value,
  onChange,
  fill = false,
  style,
}: {
  options: readonly SegmentOption[];
  value: string;
  onChange: (value: string) => void;
  fill?: boolean;
  style?: Record<string, string>;
}) {
  return (
    <div className="vseg" style={style}>
      {options.map((option) => (
        <button
          className={value === option.value ? 'on' : ''}
          key={option.value}
          style={fill ? { flex: 1 } : undefined}
          type="button"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function CheckCell({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      className={`vtck${on ? ' on' : ''}`}
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      {on ? '✓' : ''}
    </button>
  );
}

export function ToolbarIconButton({
  title,
  onClick,
  children,
}: {
  title: string;
  /** Receives the event so callers inside a clickable row can stop it propagating. */
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
}) {
  return (
    <button className="vtib" title={title} type="button" onClick={onClick}>
      {children}
    </button>
  );
}

export function Toolbar({ left, right }: { left: ReactNode; right?: ReactNode }) {
  return (
    <div className="vtbar">
      {left}
      <div className="sp" />
      {right ?? null}
    </div>
  );
}
